import type { DataModelView } from '#core/lib/cognite/view.ts';
import { isTimeseriesRefColumn } from '#core/lib/timeseries/enrichedRef.ts';

import { ProgressQueryKnowledgeGraphTool, RenderQueryKnowledgeGraphTool } from './QueryKnowledgeGraphTool.render.tsx';
import {
  type QueryKnowledgeGraphOutput,
  type QueryKnowledgeGraphParameters,
  QueryKnowledgeGraphParametersSchema,
} from './schema.ts';
import { DescribeViewsTool } from '#plugin-chat/components/tools/describe-views/DescribeViewsTool.tsx';
import { FetchTimeseriesDatapointsTool } from '#plugin-chat/components/tools/fetch-timeseries-datapoints/FetchTimeseriesDatapointsTool.tsx';
import { TransformDatasetTool } from '#plugin-chat/components/tools/transform-dataset/TransformDatasetTool.tsx';
import { queryKnowledgeGraphStep } from '#plugin-chat/lib/data-pipeline/steps/query-knowledge-graph.ts';
import { Json, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ProducerToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';

/**
 * LLM tool for querying CDF Data Models (knowledge graph) and staging the result
 * as a typed Dataset under `outputDataset`.
 *
 * Delegates all heavy lifting — CDF query execution, filter translation,
 * retry/recovery, node flattening, timeseries enrichment, column schema
 * construction — to {@link queryKnowledgeGraphStep}. The tool layer only owns
 * the LLM-facing parameter surface, the instructions block, and formatting
 * the result envelope from the produced dataset.
 */
export const QueryKnowledgeGraphTool = createTool<
  { views: DataModelView[] },
  QueryKnowledgeGraphParameters,
  QueryKnowledgeGraphOutput
>('queryKnowledgeGraph', ({ views }) => ({
  instructions: (
    <ToolGuidance
      tool={QueryKnowledgeGraphTool}
      whenNotToUse={[`For timeseries datapoints (use ${FetchTimeseriesDatapointsTool.mention})`]}
      whenToUse={[
        'Look up entities by name (Entity view)',
        'Discover relationships (FlatHierarchy view)',
        'Get KPI data (latest datapoints), timeseries references, entity metadata (requires query reference or instanceIds)',
      ]}
    >
      <h3>Standard Knowledge Graph Query Flow</h3>
      <ol>
        <li>
          <strong>
            Mandatory rule: Always call the {DescribeViewsTool.mention} tool first for all relevant views before calling
            {QueryKnowledgeGraphTool.mention}.
          </strong>
          This is a very important step to ensure you query the correct views and reference correct column names.
        </li>
        <li>
          Use {QueryKnowledgeGraphTool.mention} to query and resolve entities (normally by name) via the Entity view to
          get instanceIds.
        </li>
        <li>
          Use {QueryKnowledgeGraphTool.mention} to query detail / agent views filtered with a reference to the previous
          query (or known instanceIds).
        </li>
        <li>
          For timeseries data, use {FetchTimeseriesDatapointsTool.mention} with a reference to the data from the
          previous query (or known externalIds).
        </li>
      </ol>

      <header>Mandatory: chain ref-typed filters with sourceRef</header>
      <p>
        For ref-typed filter columns (<code>wellString</code>, <code>well</code>, <code>field</code>,
        <code>reservoir</code>, etc.) <code>sourceRef</code> is the <strong>only</strong> way to provide a value. The
        schema does not accept literal <code>{'{space, externalId}'}</code> values, and the resolver rejects literal
        strings on ref columns at runtime. Spaces and externalIds vary by view and tenant; the only legitimate source of
        an instanceId in a filter is a column projected from a dataset returned earlier in this conversation.
      </p>
      <p>
        For value-bearing ops (<code>equals</code>/<code>notEquals</code>/<code>in</code>/<code>notIn</code>/numeric
        comparisons), <code>value</code> is either a literal (<code>{'{ string }'}</code>, <code>{'{ number }'}</code>,
        <code>{'{ date }'}</code>, <code>{'{ array }'}</code>) or a <code>sourceRef</code> projection (
        <code>{'{ inputDataset, column }'}</code>) from a previously staged dataset.
      </p>
      <ul>
        <li>
          <code>sourceRef.column: "$instanceId"</code> — virtual column that projects each row's
          <code>{'{space, externalId}'}</code> identity as a ref value. Use for ref-typed filter targets.
        </li>
        <li>Named columns must match the target type: string→string, number→number, ref→ref&lt;...&gt;, etc.</li>
        <li>
          For <code>equals</code>/<code>notEquals</code> and numeric/date comparisons, the projected column must contain
          exactly one non-null value — the resolver errors otherwise. <code>in</code>/<code>notIn</code> accept any
          number.
        </li>
        <li>
          If a ref-typed query returns 0 rows and you did <em>not</em> chain via <code>sourceRef</code>, that's not a
          "no data" result — it's a guess. Re-query Entity to obtain real instanceIds and chain with
          <code>sourceRef</code> before concluding the data does not exist.
        </li>
      </ul>

      <Json
        value={{
          viewExternalId: 'FlatHierarchy_Agent',
          filters: {
            type: 'and',
            filters: [
              {
                column: 'field',
                op: 'in',
                value: { inputDataset: 'some_field_entity', column: 'string' },
              },
            ],
          },
          outputDataset: 'some_field_strings',
        }}
      />

      <header>Column names</header>
      <p>
        <code>sort.column</code> and leaf <code>filter.column</code> accept
        <strong>only top-level view property names</strong> as listed by {DescribeViewsTool.mention} — not dotted ref
        paths. For a <code>ref&lt;entity&gt;</code> column like <code>string</code>, use <code>string</code>, not
        <code>string.externalId</code>. Dotted paths like <code>well.externalId</code> are valid in
        {TransformDatasetTool.mention} (after this tool stages the dataset) but fail at the knowledge-graph query layer.
      </p>
    </ToolGuidance>
  ),
  config: {
    parameters: QueryKnowledgeGraphParametersSchema,
    render: RenderQueryKnowledgeGraphTool,
    progress: ProgressQueryKnowledgeGraphTool,
    description: <p>Query CDF Data Model views to retrieve instance data.</p>,
    produceDataset: (args, ctx) =>
      queryKnowledgeGraphStep.execute(
        {
          viewExternalId: args.viewExternalId,
          filters: args.filters,
          sort: args.sort,
          limit: args.limit,
        },
        undefined,
        { ...ctx, views },
      ),
    execute: ({ args, dataset }) => {
      // Read timeseries-ref columns directly from the schema rather than
      // sniffing column-name suffixes. The enrichment pass replaces each
      // `{ space, externalId }` ref value with a rich object carrying
      // `datapoints`, `unit`, `start`, `end`, and `latestDatapoint`; the
      // column type / refKind metadata stay the same. See
      // `EnrichedTimeseriesRef` for the full row-value shape.
      const timeseriesColumns = dataset.columns.filter(isTimeseriesRefColumn).map((c) => c.name);
      // // const columns = columnsFromView(views);
      // const viewsById = new Map(views.map((v) => [v.externalId, v]));
      // const view = viewsById.get(args.viewExternalId);
      // // const columns = columnsFromView(views);
      // console.log(dataset.columns, args, columnsFromView(view));

      const timeseriesColumnInfo =
        timeseriesColumns.length > 0
          ? `\n\nTimeseries reference columns in this dataset — each row's value can be used to retrieve datapoints in \`transformDataset\` expressions. Always derive and include units in chart legends.\n\n${timeseriesColumns.map((col) => `- ${col}`).join('\n')}`
          : '';

      const table = [
        '| Column | Type | Description |',
        '|---|---|---|',
        ...dataset.columns.map((c) => {
          const type = c.type === 'ref' ? `ref<${c.refKind ?? 'unknown'}>` : c.type;
          const description = (c.description ?? '').replace(/\n/g, ' ').replace(/\|/g, '\\|');
          return `| ${c.name} | ${type} | ${description} |`;
        }),
      ].join('\n');

      return {
        success: true as const,
        outputDataset: args.outputDataset,
        message: `Fetched ${dataset.rows.length} instances from '${args.viewExternalId}'.

Dataset schema:

${table}${timeseriesColumnInfo}

Dataset stored with key '${args.outputDataset}'. Provide '${args.outputDataset}' as the \`inputDataset\` parameter when referencing this dataset in subsequent tool calls.`,
      };
    },
  } satisfies ProducerToolConfig<QueryKnowledgeGraphParameters, QueryKnowledgeGraphOutput>,
}));
